import { useCallback, useEffect, useState } from "react";

import { AxiosError } from "axios";
import { capitalize } from "lodash-es";
import { FormProvider, useForm } from "react-hook-form";

import {
  useCreateConnection,
  useEditConnection,
  useManifestCatalog,
  useProviderManifest,
} from "src/api/connectApi/queries";
import {
  Connection,
  ConnectionCreate,
  isDatabaseProvider,
  isGenericProvider,
  LegacyIntegrationProvider,
  Provider,
} from "src/api/connectApi/types";
import { useWorkspaceWithSettings } from "src/api/queries";
import { BETA_PILL_BODY_TEXTS, BetaPill } from "src/base-components/BetaPill";
import { Spinner } from "src/base-components/Spinner";
import { ProviderSdkCatalogEntry } from "src/clients/sdk";
import { isBetaConnection } from "src/connections/CreateConnectionGrid";
import { CustomConnectionConfigForm } from "src/connections/config/CustomConnectionConfigForm";
import { SQLDatabaseConnectionConfigForm } from "src/connections/config/database/DatabaseConnectionConfigForm";
import { ProviderIcon } from "src/connections/config/Icon";
import { InboundWebhookConnectionConfigForm } from "src/connections/config/inboundWebhook/InboundWebhookConnectionConfigForm";
import { InboundWebhookConnectionCreatedForm } from "src/connections/config/inboundWebhook/InboundWebhookConnectionCreatedForm";
import { WebhookURLs } from "src/connections/config/inboundWebhook/InboundWebhookURLField";
import { ManifestForm } from "src/connections/config/manifest/ManifestForm";
import {
  IntegrationProviderConnection,
  ProviderConfigForm,
} from "src/connections/config/ProviderConfigForm";
import { ProviderSelectForm } from "src/connections/config/ProviderSelectForm";
import { RetoolConnectionConfigForm } from "src/connections/config/retool/RetoolConnectionConfigForm";
import {
  connectionInputsDefaultValues,
  connectionInputsToCreateConnection,
  connectionToConnectionInputs,
  connectionToProviderSelectInput,
  providerDefaultValues,
} from "src/connections/model/model";
import { getProviderName } from "src/connections/ProviderResourceProperties";
import { SdkScriptConfigForm } from "src/connections/sdk/SdkScriptConfigForm";
import { useConnectionManagementActions } from "src/connections/store/connectionManagementStore";
import {
  ConnectionConfigInputs,
  ConnectionInputs,
  ProviderSelectInputs,
} from "src/connections/types";
import { Modal } from "src/design-system/Modal";
import { NodeIcon } from "src/design-system/NodeIcon";
import { toastActions } from "src/design-system/Toast/utils";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { isConflictError } from "src/utils/predicates";
import { assertUnreachable } from "src/utils/typeUtils";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

export type Props = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  afterLeave?: () => void;
  connectionToEdit?: Connection;
};

export type CurrentForm = keyof ConnectionInputs;

const defaultForm = "selectProviderForm";
const EMPTY_ARRAY = [] as const;

export const EditConnectionModal: React.FC<Props> = ({
  open,
  onClose,
  workspaceId,
  afterLeave,
  connectionToEdit,
}) => {
  const sdkEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.webSdk);
  const workspace = useWorkspaceWithSettings(workspaceId);
  const { setConnectionToEdit, setModalOpen } =
    useConnectionManagementActions();
  const [currentForm, setCurrentForm] = useState<CurrentForm>(defaultForm);
  const [selectedSdkEntry, setSelectedSdkEntry] =
    useState<ProviderSdkCatalogEntry | null>(null);
  const [webhookURLs, setWebhookURLs] = useState<WebhookURLs | null>(null);
  const [providerSearchQuery, setProviderSearchQuery] = useState("");
  const createConnection = useCreateConnection(workspace.data?.base_url);

  const editConnection = useEditConnection(
    workspace.data?.base_url,
    connectionToEdit?.id,
  );
  const selectProviderForm = useForm<ProviderSelectInputs>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: providerDefaultValues(),
  });
  const configFormMethods = useForm<ConnectionConfigInputs>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: connectionInputsDefaultValues(),
  });
  const providerManifest = useProviderManifest(
    workspace.data?.base_url,
    connectionToEdit?.provider,
    connectionToEdit?.manifest_version,
  );

  const resetFormStates = useCallback(() => {
    setWebhookURLs(null);
    setSelectedSdkEntry(null);
    if (connectionToEdit != null) {
      setCurrentForm("configConnectionForm");
      if (connectionToEdit.provider === "custom") {
        const selectValues = connectionToProviderSelectInput(connectionToEdit);
        selectProviderForm.reset(selectValues);
        const configValues = connectionToConnectionInputs(connectionToEdit);
        configFormMethods.reset(configValues);
      } else {
        configFormMethods.reset(connectionInputsDefaultValues());
        selectProviderForm.reset({
          provider: connectionToEdit.provider,
          isManifestProvider: Boolean(connectionToEdit.manifest_version),
        });
      }
    } else {
      setCurrentForm("selectProviderForm");
      selectProviderForm.reset(providerDefaultValues());
      configFormMethods.reset(connectionInputsDefaultValues());
    }
  }, [setCurrentForm, selectProviderForm, configFormMethods, connectionToEdit]);

  useEffect(() => {
    // reset form when modal opens
    if (open) {
      resetFormStates();
      setProviderSearchQuery("");
    }
  }, [open, resetFormStates]);

  const webhookOnClose = (webhookURLs: WebhookURLs | null) => {
    if (webhookURLs == null) {
      onClose();
      return;
    }
    // Keeping the modal open when webhook connection is created
    // so that we can show the webhook URL to the user. Close otherwise
    setWebhookURLs(webhookURLs);
  };

  const handleEditConnection = useCallback(
    // Only used for CC
    async (
      payload: ConnectionCreate,
      connectionToEdit: Connection,
    ): Promise<boolean> => {
      try {
        await editConnection.mutateAsync({
          id: connectionToEdit.id,
          payload,
        });

        toastActions.success({
          title: "Connection edited",
          description: `Connection ${connectionToEdit.name} was edited successfully`,
        });
        return true;
      } catch {
        toastActions.failure({
          title: "Connection edit failed",
          description: `Connection ${connectionToEdit.name} could not be edited`,
        });
        return false;
      }
    },
    [editConnection],
  );

  const handleCreateConnection = useCallback(
    // Only used for CC
    async (payload: ConnectionCreate): Promise<boolean> => {
      try {
        const result = await createConnection.mutateAsync(payload);
        const toastId = toastActions.success({
          title: "Connection created",
          description: "Connection was created successfully",
          actionText: "View connection",
          onActionClick: () => {
            toastActions.dismiss(toastId);
            setConnectionToEdit(result);
            setModalOpen(true);
          },
        });
        return true;
      } catch (e) {
        if (isConflictError(e)) {
          toastActions.failure({
            title: "Connection creation failed",
            description: (e as AxiosError<{ detail: string }>).response?.data
              .detail,
            duration: Infinity,
          });
        } else {
          toastActions.failure({
            title: "Connection creation failed",
            description: "Connection could not be created.",
          });
        }
        return false;
      }
    },
    [createConnection, setConnectionToEdit, setModalOpen],
  );

  const submitData = useCallback(
    // Only used for CC
    async (data: ConnectionInputs) => {
      const payload = connectionInputsToCreateConnection(data);
      let success = false;
      if (connectionToEdit != null) {
        success = await handleEditConnection(payload, connectionToEdit);
      } else {
        success = await handleCreateConnection(payload);
      }
      if (success) {
        resetFormStates();
        onClose();
      }
    },
    [
      handleCreateConnection,
      handleEditConnection,
      connectionToEdit,
      resetFormStates,
      onClose,
    ],
  );
  const selectedProvider = selectProviderForm.getValues("provider");
  const isManifestProvider = selectProviderForm.getValues("isManifestProvider");
  const workspaceData = useWorkspaceAndOrg();
  const manifestCatalog =
    useManifestCatalog(workspaceData.workspace?.base_url).data || EMPTY_ARRAY;

  const selectedCatalogEntry =
    isManifestProvider && !connectionToEdit
      ? manifestCatalog.find((entry) => entry.name === selectedProvider)
      : undefined;
  const selectedProviderManifest = useProviderManifest(
    workspaceData.workspace?.base_url,
    selectedCatalogEntry?.name,
    selectedCatalogEntry?.version,
  );

  const manifestQuery = connectionToEdit
    ? providerManifest
    : selectedProviderManifest;
  const manifest = isManifestProvider ? manifestQuery.data : undefined;

  const renderCurrentForm = (currentForm: CurrentForm) => {
    switch (currentForm) {
      case "selectProviderForm":
        return (
          <Modal.Content noScroll>
            <FormProvider {...selectProviderForm}>
              <ProviderSelectForm
                onSdkScriptSelect={
                  sdkEnabled
                    ? (entry) => {
                        setSelectedSdkEntry(entry);
                        setCurrentForm("configConnectionForm");
                      }
                    : undefined
                }
                searchQuery={providerSearchQuery}
                onSearchQueryChange={setProviderSearchQuery}
                onSubmit={() => {
                  setCurrentForm("configConnectionForm");
                }}
              />
            </FormProvider>
          </Modal.Content>
        );
      case "configConnectionForm":
        if (!workspace.data) return null;

        if (selectedSdkEntry && !connectionToEdit) {
          return (
            <SdkScriptConfigForm
              baseUrl={workspace.data.base_url}
              entry={selectedSdkEntry}
              onBack={() => {
                setSelectedSdkEntry(null);
                setCurrentForm("selectProviderForm");
              }}
              onClose={onClose}
            />
          );
        }
        if (selectedProvider && isDatabaseProvider(selectedProvider)) {
          return (
            <SQLDatabaseConnectionConfigForm
              connection={connectionToEdit}
              provider={selectedProvider}
              workspaceUrl={workspace.data.base_url!}
              onClose={onClose}
            />
          );
        }
        if (selectedProvider === "custom") {
          if (!workspace.data) return null;
          return (
            <FormProvider {...configFormMethods}>
              <CustomConnectionConfigForm
                workspace={workspace.data}
                onSubmit={async (data) => {
                  const selectProviderData = selectProviderForm.getValues();
                  // Do not finish the form submission until
                  // the submitData function finishes.
                  await submitData({
                    selectProviderForm: selectProviderData,
                    configConnectionForm: data,
                  });
                }}
              />
            </FormProvider>
          );
        } else if (selectedProvider === "retool") {
          return (
            <RetoolConnectionConfigForm
              connection={connectionToEdit}
              workspace={workspace.data}
              onClose={onClose}
            />
          );
        } else if (selectedProvider === "webhook") {
          return (
            <InboundWebhookConnectionConfigForm
              connection={connectionToEdit}
              workspace={workspace.data}
              onClose={webhookOnClose}
            />
          );
        } else if (isManifestProvider) {
          if (!manifest) {
            // fixed height approximates the loaded form so the modal
            // doesn't expand once the manifest arrives
            return manifestQuery.isFetching ? (
              <Modal.Content>
                <div
                  className="flex h-120 items-center justify-center"
                  data-loc="manifest-form-loading"
                >
                  <Spinner />
                </div>
              </Modal.Content>
            ) : null;
          }
          return (
            <ManifestForm
              connectionToEdit={connectionToEdit}
              manifest={manifest}
              resetFormStates={resetFormStates}
              workspace={workspace.data}
              onCancelClick={() => {
                if (connectionToEdit) {
                  onClose();
                } else {
                  setCurrentForm("selectProviderForm");
                }
                resetFormStates();
              }}
              onClose={onClose}
            />
          );
        } else {
          return connectionToEdit &&
            !isDatabaseProvider(connectionToEdit.provider) &&
            !isGenericProvider(connectionToEdit.provider) ? (
            <ProviderConfigForm
              connection={
                connectionToEdit as IntegrationProviderConnection<LegacyIntegrationProvider>
              }
              type="update"
              workspace={workspace.data}
              onCancelClick={onClose}
              onClose={onClose}
            />
          ) : (
            selectedProvider != null && (
              <ProviderConfigForm
                provider={selectedProvider as LegacyIntegrationProvider}
                type="create"
                workspace={workspace.data}
                onCancelClick={() => setCurrentForm("selectProviderForm")}
                onClose={onClose}
              />
            )
          );
        }
      default:
        assertUnreachable(currentForm);
    }
  };

  let modalTitle = "Create new Connection";
  let modalProvider: Provider | string | undefined;

  if (currentForm !== "selectProviderForm") {
    if (selectedSdkEntry) {
      modalTitle = `Add a ${capitalize(selectedSdkEntry.provider)} SDK Script`;
    } else if (connectionToEdit) {
      modalTitle = connectionToEdit.name;
      modalProvider = connectionToEdit.provider;
    } else if (selectedProvider && !isManifestProvider) {
      modalTitle = `Add a ${getProviderName(
        selectedProvider as Provider,
      )} connection`;
      modalProvider = selectedProvider;
    } else if (isManifestProvider && selectedCatalogEntry) {
      modalTitle = `Add a ${selectedCatalogEntry.display_name} connection`;
      modalProvider = selectedCatalogEntry.name;
    }
  }

  if (webhookURLs && selectedProvider === "webhook") {
    return (
      <Modal open={open} onClose={onClose}>
        <Modal.Header>
          {getProviderName(selectedProvider)} created!
        </Modal.Header>
        <Modal.Content>
          <div className="px-6 pb-6">
            <InboundWebhookConnectionCreatedForm {...webhookURLs} />
          </div>
        </Modal.Content>
      </Modal>
    );
  }

  return (
    <Modal
      afterLeave={afterLeave}
      open={open}
      size={currentForm === "selectProviderForm" ? "md" : "sm"}
      onClose={onClose}
    >
      <Modal.Header
        description={
          currentForm === "selectProviderForm"
            ? "Select a provider to connect"
            : undefined
        }
        hideCloseIcon={currentForm !== "selectProviderForm"}
        icon={
          currentForm !== "selectProviderForm" && selectedSdkEntry ? (
            <NodeIcon
              alt={`${selectedSdkEntry.provider} Icon`}
              imageSrc={selectedSdkEntry.icon_path}
              size="md"
            />
          ) : (
            modalProvider && <ProviderIcon provider={modalProvider} size="md" />
          )
        }
        onClickBack={
          currentForm !== "selectProviderForm" && !connectionToEdit
            ? () => {
                setSelectedSdkEntry(null);
                setCurrentForm("selectProviderForm");
              }
            : undefined
        }
      >
        <div className="flex items-center gap-x-2">
          {modalTitle}

          {modalProvider !== undefined && isBetaConnection(modalProvider) && (
            <BetaPill
              body={BETA_PILL_BODY_TEXTS.connection}
              placement="right"
              title="Beta Connection 🎉"
            />
          )}
        </div>
      </Modal.Header>
      {renderCurrentForm(currentForm)}
    </Modal>
  );
};
